/**
 * W1-11b (app-shell-ui), revised by W3-01: workspace layout presets and screen-size rules (DESIGN.md §3.3, §4.4,
 * §4.5; ARCHITECTURE.md §6.2). Pure functions over the viewport size (plus two optional measurements), so the layout
 * is the same in a browser and in jsdom tests.
 *
 *  - Presets: Compose (default; preview 40 % of the width, the dock under it at about a quarter of the height),
 *    Animate (preview 30 %, so the motion columns get the width), Review (large preview, Problems tab open, the
 *    timeline at its minimum) and Laptop.
 *  - The docked preview is sized for its 16:9 stage: the right column gives the preview panel exactly the stage
 *    height plus its own chrome (toolbar, readout: measured by the shell, estimated otherwise) and the dock the rest;
 *    on short windows the preview width follows the height that is left after the dock's minimum (UX-001).
 *  - Laptop is chosen automatically below 1366 px wide or 650 px tall (the user's choice is kept in the settings and
 *    comes back when the window grows). Full level: the preview becomes a floating mini window (F8) and the dock and
 *    inspector a right drawer (F9). Guided level: the preview stays docked at >= 35 % and only the inspector and the
 *    dock move into the drawer, so a beginner always sees the game; with the dock gone the preview grows into the
 *    height of the column (UX-019). The 35 % holds when the user drags the dividers too (`enforcePaneMin` with
 *    `ChartLayout.minPreview`).
 *  - Narrow tablets (below 900 px): the simplified lane + preview + inspector drawer layout at both levels: compact
 *    icon rail, the preview docked next to the lane (never floating), dock and inspector in the drawer, motion
 *    columns in their bottom drawer.
 *  - The top bar is one 40 px row at every size; below 1536 px the less-used parts (the level menu, chip words)
 *    shrink first, then the snap controls, then the status chips, then the undo/redo buttons collapse into the
 *    overflow menu, so the row never wraps.
 *  - Motion columns: full when every column gets at least MOTION_COLUMN_MIN px (estimated from the timeline width),
 *    labelled sparklines otherwise and below 1280 px, a bottom drawer below 1024 px (exposed to the timeline as
 *    `data-motion-columns`; UX-004).
 *
 * Sizes are CSS px at UI scale 1; the caller divides the viewport by `--ui-scale` first (chrome scales, the layout
 * keeps its proportions).
 */

import type { ExperienceLevel, LayoutPreset, WorkspaceId } from '../../state/types';
import type { MessageKey } from '../../i18n/types';
import type { IconName } from '../registry/types';
import { m } from './shell.messages';

/** Screen-size breakpoints (CSS px). */
export const BREAKPOINTS = {
  /** Primary design width (1920x1080 at 125 %): everything fits in the top bar. */
  wide: 1536,
  /** Laptop layout below this width (1366x768 machines). */
  laptopWidth: 1366,
  /** Laptop layout below this height. */
  laptopHeight: 650,
  /** Smallest verified width (1920x1080 at 150 %); motion columns become sparklines below it. */
  narrow: 1280,
  /** Motion columns move into a bottom drawer below this width. */
  motionDrawer: 1024,
  /** Narrow tablets: the simplified lane + preview + inspector drawer layout below this width. */
  tablet: 900,
  /** The snap controls join the overflow menu below this width. */
  topBarSnap: 1280,
  /** The status chips join the overflow menu below this width. */
  topBarChips: 1180,
  /** The undo/redo buttons join the overflow menu below this width (the last group to collapse). */
  topBarUndo: 1100,
} as const;

/** Fixed chrome sizes (CSS px at UI scale 1). */
export const CHROME = {
  topBar: 40,
  hintBar: 28,
  railFull: 48,
  railGuided: 120,
  inspector: 320,
  /** Guided shows fewer fields per section: a narrower inspector leaves the preview more room. */
  inspectorGuided: 300,
  inspectorMin: 260,
  timelineMin: 360,
  /** The note lane and a few motion columns stay usable from this width (docked Compose / Animate). */
  timelineComfort: 420,
  previewMinPx: 280,
  dockMinPx: 140,
  previewToolbar: 33,
  /** Preview toolbar in its narrow layout (panel 340-760 px wide; W2-02 toolbarLayout.ts): two rows. */
  previewToolbarNarrow: 62,
  /** Below this preview width the toolbar takes two rows (W2-02 toolbarLayout.ts WIDE_WIDTH). */
  previewToolbarOneRowMin: 760,
  /** The preview's line readout (on by default, `ext['preview.readout']`): readout line plus opacity row. */
  previewReadout: 50,
  /** The readout below `previewReadoutOneLineMin` (its values wrap to a second line). */
  previewReadoutNarrow: 70,
  previewReadoutOneLineMin: 640,
  /** Draggable divider between two splitter panes (ui/Splitter.tsx). */
  splitDivider: 6,
  timingLeft: 300,
  /** Timing bottom slot (Play with clicks, the answers, the latency line): content height, not a third of the window (W3-07 CR-3, UX-082). */
  timingBottom: 110,
  publishLeftShare: 0.5,
  miniPreviewWidth: 420,
  /** Header row of the floating mini preview (title and close button). */
  miniHeader: 28,
  /** Margin the mini preview keeps from the window edges and the bars. */
  miniMargin: 12,
  /** Timeline parts that do not shrink: time gutter with the waveform strip, and the overview strip. */
  timelineFixed: 115,
  /** Narrowest note lane the motion-column estimate leaves room for. */
  laneMin: 220,
} as const;

/** The preview never gets narrower than this share of the width at the Guided level (DESIGN.md §3.3). */
export const GUIDED_MIN_PREVIEW_SHARE = 0.35;

/** Motion columns narrower than this (estimated) are drawn as sparklines (UX-004). */
export const MOTION_COLUMN_MIN = 48;

export interface LayoutPresetDef {
  readonly id: LayoutPreset;
  readonly title: MessageKey;
  readonly description: MessageKey;
  readonly icon: IconName;
  /** Preview width as a share of the workspace width (Full level). */
  readonly previewShare: number;
  /** Minimum share in Guided (the preview never gets smaller than this for beginners). */
  readonly previewShareGuided: number;
  /** Minimum dock share of the right column's height (under the preview); the preview takes what its stage needs. */
  readonly dockShare: number;
  /** The timeline (lane + motion columns) keeps at least this width before the preview grows into it. */
  readonly timelineMin: number;
  /** Dock tab opened when the preset is applied (Review opens Problems). */
  readonly dockTab: string | null;
}

export const LAYOUT_PRESETS: Readonly<Record<LayoutPreset, LayoutPresetDef>> = {
  compose: {
    id: 'compose',
    title: m.layoutCompose,
    description: m.layoutComposeDetail,
    icon: 'Music4',
    previewShare: 0.4,
    previewShareGuided: 0.4,
    dockShare: 0.25,
    timelineMin: CHROME.timelineComfort,
    dockTab: null,
  },
  animate: {
    id: 'animate',
    title: m.layoutAnimate,
    description: m.layoutAnimateDetail,
    icon: 'Spline',
    previewShare: 0.3,
    previewShareGuided: 0.35,
    dockShare: 0.25,
    timelineMin: CHROME.timelineComfort,
    dockTab: null,
  },
  review: {
    id: 'review',
    title: m.layoutReview,
    description: m.layoutReviewDetail,
    icon: 'MonitorPlay',
    previewShare: 0.5,
    previewShareGuided: 0.5,
    dockShare: 0.3,
    timelineMin: CHROME.timelineMin,
    dockTab: 'problems',
  },
  laptop: {
    id: 'laptop',
    title: m.layoutLaptop,
    description: m.layoutLaptopDetail,
    icon: 'Laptop',
    previewShare: 0.36,
    previewShareGuided: 0.45,
    dockShare: 0.25,
    timelineMin: 520,
    dockTab: null,
  },
};

export const LAYOUT_PRESET_IDS: readonly LayoutPreset[] = ['compose', 'animate', 'review', 'laptop'];

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** Viewport in layout px (CSS px divided by the UI scale). */
export function layoutViewport(v: Viewport, uiScale: number): Viewport {
  const s = Number.isFinite(uiScale) && uiScale > 0 ? uiScale : 1;
  return { width: v.width / s, height: v.height / s };
}

/** True when the screen is too small for the docked layouts (Laptop applies automatically). */
export function needsLaptop(v: Viewport): boolean {
  return v.width < BREAKPOINTS.laptopWidth || v.height < BREAKPOINTS.laptopHeight;
}

/** The preset in effect: the user's choice, or Laptop on small screens. */
export function effectiveLayout(chosen: LayoutPreset, v: Viewport): LayoutPreset {
  return needsLaptop(v) ? 'laptop' : chosen;
}

/**
 * Which top-bar groups shrink or move into the overflow menu at this width (DESIGN.md §4.4). The least-used parts go
 * first (UX-011): below 1536 px the level menu joins the overflow and the save chip drops its words (its colour,
 * icon and tooltip stay); the snap controls, used all the time while charting, stay up to 1280 px; then the status
 * chips (below 1180 px) and undo / redo (below 1100 px) follow.
 */
export interface TopBarCollapse {
  readonly level: boolean;
  readonly chipText: boolean;
  readonly snap: boolean;
  readonly chips: boolean;
  readonly undo: boolean;
}

export function topBarCollapse(width: number): TopBarCollapse {
  return {
    level: width < BREAKPOINTS.wide,
    chipText: width < BREAKPOINTS.wide,
    snap: width < BREAKPOINTS.topBarSnap,
    chips: width < BREAKPOINTS.topBarChips,
    undo: width < BREAKPOINTS.topBarUndo,
  };
}

export type MotionColumnsMode = 'full' | 'sparklines' | 'drawer';

/** Pixel layout of the Chart workspace (columns left to right). */
export interface ChartLayout {
  readonly preset: LayoutPreset;
  readonly width: number;
  readonly height: number;
  /** Height of the workspace area (viewport minus top bar and hint bar, or as measured). */
  readonly bodyHeight: number;
  readonly rail: number;
  readonly timeline: number;
  /** Docked preview column width (0 when the preview floats). */
  readonly preview: number;
  /** Inspector column width (0 when it lives in the drawer). */
  readonly inspector: number;
  readonly previewPlacement: 'docked' | 'floating';
  readonly dockPlacement: 'column' | 'drawer';
  readonly inspectorPlacement: 'column' | 'drawer';
  /** Shares of the right column's height: [preview, dock] in percent (dock 0 when it is in the drawer). */
  readonly rightColumn: readonly [number, number];
  readonly motionColumns: MotionColumnsMode;
  /** Rail shows text labels (Guided). */
  readonly railLabels: boolean;
  /** Narrow tablet: the simplified lane + preview + inspector drawer layout. */
  readonly compact: boolean;
  /** Smallest width (px) the docked preview may be resized to (Guided: 35 % of the width; else 0). */
  readonly minPreview: number;
}

export interface ChartLayoutInput {
  readonly viewport: Viewport;
  readonly preset: LayoutPreset;
  readonly level: ExperienceLevel;
  /** Measured height of the workspace area (layout px; banners excluded); default: viewport minus the two bars. */
  readonly bodyHeight?: number | undefined;
  /**
   * Measured height of the preview panel's chrome (everything but the stage: toolbar rows, readout) at its current
   * width (layout px); default: estimated from the width (`estimatePreviewChrome`).
   */
  readonly previewChrome?: number | undefined;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Estimated chrome of a preview panel this wide: toolbar (two rows below 760 px) and readout (two lines narrow). */
export function estimatePreviewChrome(width: number): number {
  const toolbar = width < CHROME.previewToolbarOneRowMin ? CHROME.previewToolbarNarrow : CHROME.previewToolbar;
  const readout = width < CHROME.previewReadoutOneLineMin ? CHROME.previewReadoutNarrow : CHROME.previewReadout;
  return toolbar + readout;
}

/** Widest 16:9 stage whose height fits `height` px. */
function widthForStageHeight(height: number): number {
  return Math.floor((Math.max(0, height) * 16) / 9);
}

/** Estimated width of one motion column: what the timeline has left after its fixed parts and the lane. */
export function motionColumnWidth(timeline: number, level: ExperienceLevel): number {
  // Guided shows Position as one X | Y pair plus rotation, opacity and speed; Full shows X and Y apart.
  const columns = level === 'guided' ? 4 : 5;
  return (timeline - CHROME.timelineFixed - CHROME.laneMin) / columns;
}

/** The Chart workspace layout for a viewport, preset (already resolved by `effectiveLayout`) and level. */
export function computeChartLayout(input: ChartLayoutInput): ChartLayout {
  const { viewport, level } = input;
  const preset = effectiveLayout(input.preset, viewport);
  const def = LAYOUT_PRESETS[preset];
  const width = Math.max(0, viewport.width);
  const measuredBody = input.bodyHeight;
  const bodyHeight = Math.max(
    0,
    measuredBody !== undefined && Number.isFinite(measuredBody) && measuredBody > 0
      ? measuredBody
      : viewport.height - CHROME.topBar - CHROME.hintBar,
  );
  const measuredChrome = input.previewChrome;
  const chromeFor = (w: number): number =>
    measuredChrome !== undefined && Number.isFinite(measuredChrome) && measuredChrome >= 0
      ? measuredChrome
      : estimatePreviewChrome(w);
  const guided = level === 'guided';
  const compact = width < BREAKPOINTS.tablet;
  const rail = guided && !compact ? CHROME.railGuided : CHROME.railFull;
  const laptop = preset === 'laptop';
  const previewFloats = laptop && !guided && !compact;
  const inspectorInDrawer = laptop;
  let inspector = inspectorInDrawer ? 0 : guided ? CHROME.inspectorGuided : CHROME.inspector;
  const room = width - rail - inspector;
  const timelineFloor = Math.min(def.timelineMin, Math.max(CHROME.timelineMin, room - CHROME.previewMinPx));
  const guidedMin = guided && !previewFloats ? Math.round(width * GUIDED_MIN_PREVIEW_SHARE) : 0;
  const floorPreview = Math.max(Math.min(CHROME.previewMinPx, width), guidedMin);
  let preview = 0;
  if (!previewFloats) {
    const share = guided ? Math.max(def.previewShare, def.previewShareGuided) : def.previewShare;
    let target = Math.round(width * share);
    if (!laptop) {
      // The dock keeps its share of the height; the stage gets what is left above it.
      const dockMin = Math.max(CHROME.dockMinPx, bodyHeight * def.dockShare);
      target = Math.min(target, widthForStageHeight(bodyHeight - CHROME.splitDivider - dockMin - chromeFor(target)));
    }
    preview = Math.max(target, floorPreview);
  }
  let timeline = room - preview;
  if (timeline < timelineFloor && !previewFloats) {
    // Give the lane its minimum from the preview, but never below the Guided minimum (a beginner always sees the
    // game) nor the preview's own minimum; then from the inspector, down to its minimum.
    const take = Math.min(timelineFloor - timeline, Math.max(0, preview - floorPreview));
    preview -= take;
    timeline += take;
  }
  // Laptop: the dock stays under a docked preview when the column has room for it (Guided, UX-019); otherwise it
  // joins the inspector in the drawer and the preview grows into the column's height, as long as the timeline keeps
  // its width (twice: the chrome shrinks when the toolbar fits one row).
  let dockInDrawer = laptop;
  if (laptop && !previewFloats && !compact) {
    const left = bodyHeight - CHROME.splitDivider - chromeFor(preview) - (preview * 9) / 16;
    if (left >= CHROME.dockMinPx + 20) dockInDrawer = false;
  }
  if (dockInDrawer && !previewFloats) {
    for (let i = 0; i < 2; i++) {
      const byHeight = widthForStageHeight(bodyHeight - chromeFor(preview));
      preview = Math.max(preview, Math.min(byHeight, room - timelineFloor));
    }
    timeline = room - preview;
  }
  if (timeline < CHROME.timelineMin && inspector > 0) {
    const take = Math.min(CHROME.timelineMin - timeline, inspector - CHROME.inspectorMin);
    inspector -= Math.max(0, take);
    timeline += Math.max(0, take);
  }
  timeline = Math.max(0, timeline);
  // Right column: the preview panel gets its 16:9 stage plus its chrome (the dock keeps at least its minimum); the
  // dock takes the rest. Shares are of the height without the divider; the pane is a little narrower than `preview`
  // (the main splitter's dividers), so its stage is computed for that width.
  let rightColumn: readonly [number, number] = [100, 0];
  if (!dockInDrawer && !previewFloats && bodyHeight > 0) {
    const panes = inspector > 0 ? 3 : 2;
    const total = timeline + preview + inspector;
    const paneWidth = total > 0 ? (preview * (total - CHROME.splitDivider * (panes - 1))) / total : preview;
    const available = Math.max(1, bodyHeight - CHROME.splitDivider);
    const wanted = Math.ceil((paneWidth * 9) / 16 + chromeFor(preview));
    const previewPx = clamp(wanted, available * 0.3, Math.max(available * 0.3, available - CHROME.dockMinPx));
    const previewPct = clamp((previewPx / available) * 100, 30, 85);
    rightColumn = [round3(previewPct), round3(100 - previewPct)];
  }
  const motionColumns: MotionColumnsMode =
    width < BREAKPOINTS.motionDrawer
      ? 'drawer'
      : width < BREAKPOINTS.narrow || motionColumnWidth(timeline, level) < MOTION_COLUMN_MIN
        ? 'sparklines'
        : 'full';
  return {
    preset,
    width,
    height: viewport.height,
    bodyHeight,
    rail,
    timeline,
    preview,
    inspector,
    previewPlacement: previewFloats ? 'floating' : 'docked',
    dockPlacement: dockInDrawer ? 'drawer' : 'column',
    inspectorPlacement: inspectorInDrawer ? 'drawer' : 'column',
    rightColumn,
    motionColumns,
    railLabels: guided && !compact,
    compact,
    minPreview: guided && !previewFloats ? Math.min(preview, guidedMin) : 0,
  };
}

/**
 * Splitter sizes (percent) after a resize, with pane `index` kept at `minPct` or more: what it lacks is taken back
 * from the panes that grew in the move (the divider was dragged into it), then from the largest. Panes keep at least
 * 1 %. `prev` are the sizes before the move (pass `next` itself to fix stored sizes).
 */
export function enforcePaneMin(
  prev: readonly number[],
  next: readonly number[],
  index: number,
  minPct: number,
): number[] {
  const out = [...next];
  const current = out[index];
  if (current === undefined || !(minPct > 0) || current >= minPct - 1e-9) return out;
  let deficit = minPct - current;
  const others = out
    .map((_, i) => i)
    .filter((i) => i !== index)
    .sort((a, b) => {
      const growth = (out[b] ?? 0) - (prev[b] ?? 0) - ((out[a] ?? 0) - (prev[a] ?? 0));
      return growth > 1e-9 || growth < -1e-9 ? growth : (out[b] ?? 0) - (out[a] ?? 0);
    });
  for (const i of others) {
    if (deficit <= 1e-9) break;
    const v = out[i] ?? 0;
    const take = Math.min(deficit, Math.max(0, v - 1));
    out[i] = v - take;
    deficit -= take;
  }
  out[index] = minPct - deficit;
  return out;
}

/** Percent of the Chart splitter (timeline, preview, inspector) the preview pane may not go below (0: no limit). */
export function previewMinPct(l: ChartLayout): number {
  const total = l.timeline + l.preview + l.inspector;
  return l.minPreview > 0 && total > 0 && l.previewPlacement === 'docked' ? round3((l.minPreview / total) * 100) : 0;
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Splitter sizes (percent) of the resizable part of the Chart workspace, left to right: timeline, right column
 * (preview + dock) and inspector (when docked). The tool rail is fixed and not part of it.
 */
export function chartSplitSizes(l: ChartLayout): number[] {
  const total = l.timeline + l.preview + l.inspector;
  if (total <= 0) return l.inspector > 0 ? [60, 20, 20] : [60, 40];
  const pct = (v: number): number => round3((v / total) * 100);
  if (l.previewPlacement === 'floating') return l.inspector > 0 ? [pct(l.timeline), pct(l.inspector)] : [100];
  return l.inspector > 0 ? [pct(l.timeline), pct(l.preview), pct(l.inspector)] : [pct(l.timeline), pct(l.preview)];
}

/** Layout of the Song & Timing workspace: left column px and [main, bottom] height shares. */
export interface TimingLayout {
  readonly left: number;
  readonly main: number;
  readonly rows: readonly [number, number];
}

export function computeTimingLayout(v: Viewport, bodyHeight?: number): TimingLayout {
  const left = v.width < BREAKPOINTS.narrow ? 260 : CHROME.timingLeft;
  // The bottom slot is as tall as its content (Play with clicks, the answers, the latency line), not a third of the
  // window (W3-07 CR-3, UX-082). The share is of the measured body height; without one, of the viewport.
  const total = bodyHeight !== undefined && bodyHeight > 0 ? bodyHeight : Math.max(1, v.height);
  const bottom = clamp(Math.round((CHROME.timingBottom / total) * 100), 10, 30);
  return { left, main: Math.max(0, v.width - left), rows: [100 - bottom, bottom] };
}

/** Layout of the Publish workspace: [left, right] width shares. */
export function computePublishLayout(v: Viewport): readonly [number, number] {
  const left = v.width < BREAKPOINTS.narrow ? 0.45 : CHROME.publishLeftShare;
  return [Math.round(left * 100), Math.round((1 - left) * 100)];
}

/** Workspace a slot belongs to (null for the global slots). */
export function workspaceOfSlot(slot: string): WorkspaceId | null {
  if (slot.startsWith('chart.')) return 'chart';
  if (slot.startsWith('timing.')) return 'timing';
  if (slot.startsWith('publish.')) return 'publish';
  return null;
}

/** Key under which user-resized sizes are remembered (per preset and level, so Guided and Full differ). */
export function layoutSizesKey(workspace: WorkspaceId, preset: LayoutPreset, level: ExperienceLevel): string {
  return `${workspace}:${preset}:${level}`;
}

/** Sanitises stored splitter sizes: the right count, finite, positive; null when unusable. */
export function sanitizeSizes(v: unknown, count: number): number[] | null {
  if (!Array.isArray(v) || v.length !== count) return null;
  const out: number[] = [];
  for (const x of v) {
    if (typeof x !== 'number' || !Number.isFinite(x) || x <= 0) return null;
    out.push(x);
  }
  return out;
}
